import { arrayMove } from '@dnd-kit/sortable';
import type { ColumnConfigEntry, ColumnSorting } from '@erp/shared-types';
import type { SortOption } from '@/components/procurement/query-builder/SortPanel';
import type { SortDraft } from '@/components/procurement/query-builder/useQueryBuilderState';

/** The most levels a saved sort holds (the schema's cap). */
export const MAX_SORT_LEVELS = 10;

/** Columns a sort level can use: shown, and not opted out of sorting. */
export function sortOptions(columns: ColumnConfigEntry[]): SortOption[] {
  return columns.flatMap((column) =>
    !column.hidden && column.sortable !== false
      ? [{ value: column.id, label: column.header }]
      : [],
  );
}

export function sortDrafts(sorting: ColumnSorting): SortDraft[] {
  return sorting.map((level) => ({
    id: level.id,
    field: level.id,
    direction: level.desc ? 'desc' : 'asc',
  }));
}

/** Append the first option not already sorted on, ascending. */
export function addSortLevel(
  sorting: ColumnSorting,
  options: SortOption[],
  maxLevels = MAX_SORT_LEVELS,
): ColumnSorting {
  if (sorting.length >= maxLevels) return sorting;
  const used = new Set(sorting.map((level) => level.id));
  const next = options.find((option) => !used.has(option.value));
  return next ? [...sorting, { id: next.value, desc: false }] : sorting;
}

export function removeSortLevel(
  sorting: ColumnSorting,
  id: string,
): ColumnSorting {
  return sorting.filter((level) => level.id !== id);
}

export function moveSortLevel(
  sorting: ColumnSorting,
  fromIndex: number,
  toIndex: number,
): ColumnSorting {
  return arrayMove(sorting, fromIndex, toIndex);
}

/**
 * Change one level's column or direction. Clearing the column removes the
 * level; picking a column another level uses moves that column here, since a
 * column sorts once.
 */
export function changeSortLevel(
  sorting: ColumnSorting,
  id: string,
  patch: Partial<Omit<SortDraft, 'id'>>,
): ColumnSorting {
  if (patch.field === '') return removeSortLevel(sorting, id);
  const field = patch.field ?? id;
  return sorting.flatMap((level) => {
    if (level.id !== id) return level.id === field ? [] : [level];
    const desc =
      patch.direction === undefined ? level.desc : patch.direction === 'desc';
    return [{ id: field, desc }];
  });
}

/**
 * The sort to save after an edit made on the active columns only: the edited
 * levels, then the saved levels on columns not active right now (hidden for
 * the moment), so a passing filter does not erase them. The hidden levels
 * count toward the level cap; an edit past it loses its last levels instead.
 */
export function withInactiveLevels(
  next: ColumnSorting,
  saved: ColumnSorting,
  activeIds: ReadonlySet<string>,
): ColumnSorting {
  const kept = new Set(next.map((level) => level.id));
  const inactive = saved.filter(
    (level) => !activeIds.has(level.id) && !kept.has(level.id),
  );
  return [...next.slice(0, MAX_SORT_LEVELS - inactive.length), ...inactive];
}

/** How many levels the active columns can sort by, beside the hidden ones. */
export function activeSortLimit(
  saved: ColumnSorting,
  activeIds: ReadonlySet<string>,
): number {
  return (
    MAX_SORT_LEVELS - saved.filter((level) => !activeIds.has(level.id)).length
  );
}

/** The saved levels whose columns are active. */
export function activeLevels(
  sorting: ColumnSorting,
  activeIds: ReadonlySet<string>,
): ColumnSorting {
  return sorting.filter((level) => activeIds.has(level.id));
}
